import { currentUrl, pushState, replaceState, URLX } from '@avensia/excite';
import * as React from 'react';

export const SEARCH_PARAMS_PAGE = 'p';

export const SEARCH_PARAMS_COUNT = 'count';
export const SEARCH_NOTIFY_QUERY_NAME = 'notify';

export function getPaginationUrl(page: number): URLX {
  const url = currentUrl();
  url.searchParams.delete(SEARCH_PARAMS_PAGE);
  if (page > 1) {
    url.searchParams.append(SEARCH_PARAMS_PAGE, String(page));
  }

  return url;
}

export function updateUrlWithPaginationSearchParams(currentPage: number) {
  return pushState(getPaginationUrl(currentPage), { skipUpdateScrollPosition: true });
}

export function updateUrlWithSearchParamsForBoolean(checked: boolean, attribute: string) {
  const url = getFacetUrl();
  url.searchParams.delete(attribute);
  if (checked) {
    url.searchParams.append(attribute, String(checked));
  }

  return replaceState(url);
}

export function updateUrlWithSearchParamsForRange(range: Excite.Esales.RangeApiModel, attribute: string) {
  const url = getFacetUrl();
  if (range.max === range.maxSelected && range.min === range.minSelected){
    url.searchParams.delete(attribute)
  }else {
    url.searchParams.set(attribute, `[${range.minSelected},${range.maxSelected}]`);
  }
  return replaceState(url);
}

function allSelectedValues(
  values: Excite.Esales.FacetValueApiModel[],
  selectedValues: Excite.Esales.FacetValueApiModel[] = [],
) {
  selectedValues.push(...values.filter(v => v.selected));
  return selectedValues;
}

function updateUrlWithFacetSearchParams(facets: Excite.Esales.FacetApiModel[]) {
  const url = getFacetUrl();

  for (const facet of facets) {
    if (!facet.isRange) {
      url.searchParams.delete(facet.attribute);
      for (const selected of allSelectedValues(facet.values)) {
        url.searchParams.append(facet.attribute, selected.text);
      }
    }
  }
  return replaceState(url, { shouldUseCache: false });
}

function getFacetUrl() {
  const url = currentUrl();
  url.hiddenParams.set(SEARCH_NOTIFY_QUERY_NAME, 'true');
  url.searchParams.delete(SEARCH_PARAMS_PAGE);
  url.searchParams.delete(SEARCH_PARAMS_COUNT);
  return url;
}

function clearFacets(facets: Excite.Esales.FacetApiModel[]) {
  const url = currentUrl();

  url.searchParams.delete(SEARCH_PARAMS_PAGE);
  url.searchParams.delete(SEARCH_PARAMS_COUNT);

  for (const facet of facets) {
    url.searchParams.delete(facet.attribute);
  }
  return replaceState(url, { shouldUseCache: false });
}

export function useFacetsSelection(
  facets: Excite.Esales.FacetApiModel[],
  config?: { onUrlUpdate?: (facets: Excite.Esales.FacetApiModel[]) => unknown },
): [Excite.Esales.FacetApiModel[], (facet: Excite.Esales.FacetApiModel) => void, () => void] {
  const lastFacetsRef = React.useRef(facets);
  const [facetsState, setFacets] = React.useState(facets);
  const configuredOnUrlUpdate = config?.onUrlUpdate;

  React.useEffect(() => {
    if (lastFacetsRef.current !== facets) {
      setFacets(facets);
      lastFacetsRef.current = facets;
    }
  }, [facets]);

  const clearFacetValues = React.useCallback(() => {
    const newFacets = facetsState.map(facet => {
      if (facet.isRange) {
        return {
          ...facet,
          range: {
            max: facet.range.max,
            maxSelected: facet.range.max,
            minSelected: facet.range.min,
            min: facet.range.min,
          },
        };
      } else {
        const values = facet.values.map(value => ({ ...value, selected: false }));
        return { ...facet, values };
      }
    });
    setFacets(newFacets);
    configuredOnUrlUpdate ? configuredOnUrlUpdate(newFacets) : clearFacets(newFacets);
  }, [configuredOnUrlUpdate, facetsState]);

  const onFacetValueChange = React.useCallback(
    (facet: Excite.Esales.FacetApiModel) => {
      const index = facetsState.findIndex(f => f.attribute === facet.attribute);
      facetsState[index] = { ...facet };
      setFacets(facetsState);
      if (configuredOnUrlUpdate) {
        configuredOnUrlUpdate(facetsState);
      } else {
        updateUrlWithFacetSearchParams(facetsState);
      }
    },
    [facetsState, configuredOnUrlUpdate],
  );

  return [facetsState, onFacetValueChange, clearFacetValues];
}
